
import React from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import { InvestorKPI } from "@/lib/types";

interface InvestorMetricsProps {
  kpis: InvestorKPI[];
  setKpis: React.Dispatch<React.SetStateAction<InvestorKPI[]>>;
  latestValuation: number;
  totalRaised: number;
  currentCashBalance: number;
  monthlyBurn: number;
  runway: number;
}

export function InvestorMetrics({ 
  kpis, 
  setKpis,
  latestValuation,
  totalRaised,
  currentCashBalance,
  monthlyBurn,
  runway
}: InvestorMetricsProps) {
  // Generate sample chart data for demonstration
  const valuationData = [
    { name: "Pre-seed", valuation: 1000000 },
    { name: "Seed", valuation: 2500000 },
    { name: "Current", valuation: latestValuation },
    { name: "Projected", valuation: latestValuation * 1.5 }
  ];
  
  const cashflowData = [
    { month: "Jan", balance: 400000, burn: 30000 },
    { month: "Feb", balance: 370000, burn: 30000 },
    { month: "Mar", balance: 340000, burn: 30000 },
    { month: "Apr", balance: 310000, burn: 25000 },
    { month: "May", balance: 285000, burn: 25000 },
    { month: "Jun", balance: 260000, burn: 25000 },
    { month: "Jul", balance: 235000, burn: 25000 },
    { month: "Aug", balance: 210000, burn: 25000 },
    { month: "Sep", balance: 185000, burn: 25000 },
    { month: "Oct", balance: 160000, burn: 25000 },
    { month: "Nov", balance: 135000, burn: 25000 },
    { month: "Dec", balance: 110000, burn: 25000 },
  ];
  
  // Group KPIs by category
  const financialKPIs = kpis.filter(kpi => kpi.category === "financial");
  const valuationKPIs = kpis.filter(kpi => kpi.category === "valuation");
  const operationalKPIs = kpis.filter(kpi => kpi.category === "operational" || kpi.category === "growth");
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Valuation Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Valuation Growth</CardTitle>
            <CardDescription>
              Company valuation across funding rounds
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={valuationData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis 
                    tickFormatter={(value) => 
                      `$${(value >= 1000000 
                        ? (value / 1000000).toFixed(1) + 'M' 
                        : (value / 1000).toFixed(0) + 'K')}`
                    } 
                  />
                  <Tooltip 
                    formatter={(value) => 
                      [`$${Number(value).toLocaleString()}`, "Valuation"]
                    } 
                  />
                  <Bar dataKey="valuation" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        
        {/* Cash Runway Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Cash Runway</CardTitle>
            <CardDescription>
              Cash balance and burn rate projection
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={cashflowData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => 
                      `$${(value >= 1000000 
                        ? (value / 1000000).toFixed(1) + 'M' 
                        : (value / 1000).toFixed(0) + 'K')}`
                    } 
                  />
                  <Tooltip 
                    formatter={(value) => 
                      [`$${Number(value).toLocaleString()}`, "Amount"]
                    } 
                  />
                  <Legend />
                  <Line type="monotone" dataKey="balance" stroke="#8884d8" name="Cash Balance" />
                  <Line type="monotone" dataKey="burn" stroke="#ff7300" name="Monthly Burn" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Key Metrics Cards */}
      <div>
        <h3 className="text-lg font-medium mb-3">Financial KPIs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {financialKPIs.map((kpi) => (
            <MetricCard key={kpi.id} kpi={kpi} />
          ))}
        </div>
        
        <h3 className="text-lg font-medium mb-3">Valuation Metrics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {valuationKPIs.map((kpi) => (
            <MetricCard key={kpi.id} kpi={kpi} />
          ))}
        </div>
        
        <h3 className="text-lg font-medium mb-3">Operational Metrics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          {operationalKPIs.map((kpi) => (
            <MetricCard key={kpi.id} kpi={kpi} />
          ))}
        </div>
      </div>
    </div>
  );
}

// Helper component for individual KPI cards
function MetricCard({ kpi }: { kpi: InvestorKPI }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div className="text-sm font-medium text-muted-foreground">{kpi.name}</div>
          {kpi.changePercentage !== undefined && (
            <Badge 
              variant="outline" 
              className={
                kpi.changePercentage > 0 
                  ? "bg-green-50 text-green-700 border-green-200" 
                  : kpi.changePercentage < 0 
                    ? "bg-red-50 text-red-700 border-red-200"
                    : "bg-gray-50 text-gray-700 border-gray-200"
              }
            >
              <span className="flex items-center">
                {kpi.changePercentage > 0 ? (
                  <ArrowUpIcon className="h-3 w-3 mr-1" />
                ) : kpi.changePercentage < 0 ? (
                  <ArrowDownIcon className="h-3 w-3 mr-1" />
                ) : null}
                {Math.abs(kpi.changePercentage).toFixed(1)}%
              </span>
            </Badge>
          )}
        </div>
        <div className="text-2xl font-bold mt-1">
          {kpi.unit === "NZD" || kpi.unit === "NZD/month" ? "$" : ""}
          {typeof kpi.value === "number" && kpi.value >= 1000000 && kpi.unit === "NZD" 
            ? (kpi.value / 1000000).toFixed(1) + "M" 
            : typeof kpi.value === "number" && kpi.value >= 1000 && (kpi.unit === "NZD" || kpi.unit === "NZD/month")
              ? (kpi.value / 1000).toFixed(0) + "K"
              : kpi.value}
          {kpi.unit !== "NZD" && kpi.unit !== "NZD/month" ? " " + kpi.unit : ""}
        </div>
        <div className="mt-2 text-xs text-muted-foreground flex items-center justify-between">
          <span>Target: {kpi.target}</span>
          <span className={
            kpi.value >= kpi.target 
              ? "text-green-600" 
              : kpi.value >= kpi.target * 0.8 
                ? "text-amber-600" 
                : "text-red-600"
          }>
            {((kpi.value / kpi.target) * 100).toFixed(0)}% of target
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
